import { EmailService } from '@/shared/email.service';
import { UtilFns } from '@/shared/util-fns';
import { Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Account, ReturnRequestDetail } from 'app/model/entities/entity-model';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { environment } from 'environments/environment';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { RestockFeeInvoiceComponent } from './restock-fee-invoice.component';

export interface RestockFeeDialogParams extends DialogParams {
  account: Account;
  returnRequestDetails: ReturnRequestDetail[];
  index: number,
  totalCount: number,
}

@Component({
    templateUrl: './restock-fee-dialog.component.html',
    standalone: false
})
export class RestockFeeDialogComponent extends DialogBaseComponent<RestockFeeDialogParams> {
  @ViewChild('form') form: NgForm;

  account: Account;
  returnRequestDetails: ReturnRequestDetail[];
  emailAddress: string;
  pdfurl: string;

  constructor(
    public config: DynamicDialogConfig<any>, public dialogRef: DynamicDialogRef, 
      public domainService: DomainService, private emailService: EmailService,
  ) {
    super(config, dialogRef, domainService);
    const header = `Invoice Restock Fees - ${this.params.index} of ${this.params.totalCount}`;
    Object.assign(this.config, {
      header: header,
      width: '870px',
      height: '90vh',
    });
    this.prepare();
  }

  private async prepare() {
    const rrds = this.params.returnRequestDetails;
    this.returnRequestDetails = rrds;
    this.account = this.params.account;

    const jo = this.returnRequestDetails[0].joDetail.joHeader;
    this.emailAddress = jo.billingEmail || this.account.invoiceEmail || jo.empEmail;

    const rrids = rrds.map(x => x.id).join(',');
    const invoiceName = RestockFeeInvoiceComponent.getInvoiceName(rrds);
    this.pdfurl = environment.apiRoot + "api/pdf/pdffromurl/Invoice-" + invoiceName +
      "?url=" + UtilFns.getBaseUrl() + 
      "restock-fee-invoice/" + this.account.id + "?rrids=" + rrids;

    this.isPageReady = true;
  }

  /** post to accounting system - updates ReturnRequestRetails with new processNotes */
  async postInvoice() {
    let result;
    await this.wrapBusy(async () => {    
      result = await this.postRestockFees(this.params.returnRequestDetails);
    });
    if (!result.ok) {
      this.dbSaveService.rejectChanges();
      await this.dialogService.showOkMessage("Invoice NOT yet posted", result.err);
    } else {
      await this.dialogService.showOkMessage("Invoice posted", "Restock fees invoiced.");
      this.dialogRef.close(true);
    }
    return result.ok;
  }

  /** Post the restock fee invoice to accounting.  This updates the RestockFeeInvoiced field to show that the ReturnRequestDetails have been invoiced. */
  private async postRestockFees(rrDetails: ReturnRequestDetail[]) {
    const rrdIds = rrDetails.map((x) => `rrdIds=${x.id}`).join("&");
    const invoiceName = RestockFeeInvoiceComponent.getInvoiceName(rrDetails);
    const url = environment.apiRoot + `api/quickbooks/PostRestockInvoiceToGL?accountId=${this.account.id}&invoiceName=${invoiceName}&${rrdIds}`;
    const query = this.uow.createQuery(ReturnRequestDetail, url);
    try {
      // post to accounting system - updates ReturnRequestRetails in cache
      await query.execute(true);
      if (rrDetails.every((x) => x.restockFeeInvoiced)) {
        return { ok: true, err: null };
      } else {
        return { ok: false, err: "Unable to post" };
      }
    } catch (err) {
      console.log("Error posting invoice to " + url, err);
      return { ok: false, err: err };
    }
  }

  printInvoice() {
    window.open(this.pdfurl, "_blank");
  }

  async emailInvoice() {
    if (!this.canSendEmail) {
      return;
    }
    const invoiceName = RestockFeeInvoiceComponent.getInvoiceName(this.returnRequestDetails);
    const rrids = this.returnRequestDetails.map(x => x.id).join(',');
    const status = await this.emailService.sendRestockInvoiceEmail(this.account.id, rrids, invoiceName, this.emailAddress);

    if (status === "accepted" || status === "stored") {
      this.toastr.info(status + "; checking for delivery", "Email Status");
      setTimeout(() => this.getEmailStatus(), 3000);
    } else {
      this.toastr.error(status, "Email Status");
    }

  }

  /** This will update invoiceHeader.invoiceStatusId to Printed if email was successful  */
  private async getEmailStatus() {
    const invoiceName = RestockFeeInvoiceComponent.getInvoiceName(this.returnRequestDetails);
    const email = await this.emailService.getRestockInvoiceEmailStatus(this.account.id, invoiceName);
    if (email) {
      this.toastr.info(email.status, "Email Status");
    }
  }

  get canSendEmail(): boolean {
    if (!this.emailAddress) {
      return false;
    }
    return UtilFns.validateMultipleEmail(this.emailAddress);
  }

  get isPosted(): boolean {
    return !this.returnRequestDetails.some(x => !x.restockFeeInvoiced);
  }

  async cancel() {
    this.dialogRef.close(false);
  }


}
